// @flow
import get from 'lodash/get';

type Modifier = 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey';

type KeyMapping = {|
  key: string,
  modifiers?: Array<Modifier>,
|};

export const KEY_MAPPINGS = {
  ARROW_UP: {
    key: 'ArrowUp',
  },
  ARROW_DOWN: {
    key: 'ArrowDown',
  },
  ARROW_LEFT: {
    key: 'ArrowLeft',
  },
  ARROW_RIGHT: {
    key: 'ArrowRight',
  },
  ENTER: {
    key: 'Enter',
  },
  ESCAPE: {
    key: 'Escape',
  },
  BACKSPACE: {
    key: 'Backspace',
  },
  TAB: {
    key: 'Tab',
  },
  SHIFT_TAB: {
    key: 'Tab',
    modifiers: ['shiftKey'],
  },
  CTRL_V: {
    key: 'v',
    modifiers: ['ctrlKey'],
  },
  CMD_V: {
    key: 'v',
    modifiers: ['metaKey'],
  },
  CTRL_U: {
    key: 'u',
    modifiers: ['ctrlKey'],
  },
  CTRL_W: {
    key: 'w',
    modifiers: ['ctrlKey'],
  },
  ALT_BACKSPACE: {
    key: 'Backspace',
    modifiers: ['altKey'],
  },
  CTRL_L: {
    key: 'l',
    modifiers: ['ctrlKey'],
  },
  CMD_K: {
    key: 'k',
    modifiers: ['metaKey'],
  },
  CTRL_A: {
    key: 'a',
    modifiers: ['ctrlKey'],
  },
  CMD_ARROW_LEFT: {
    key: 'ArrowLeft',
    modifiers: ['metaKey'],
  },
  CTRL_ARROW_LEFT: {
    key: 'ArrowLeft',
    modifiers: ['ctrlKey'],
  },
  CTRL_E: {
    key: 'e',
    modifiers: ['ctrlKey'],
  },
  CMD_ARROW_RIGHT: {
    key: 'ArrowRight',
    modifiers: ['metaKey'],
  },
  CTRL_ARROW_RIGHT: {
    key: 'ArrowRight',
    modifiers: ['ctrlKey'],
  },
  CTRL_LEFT_BRACKET: {
    key: '[',
    modifiers: ['ctrlKey'],
  },
  CMD_LEFT_BRACKET: {
    key: '[',
    modifiers: ['metaKey'],
  },
  CTRL_RIGHT_BRACKET: {
    key: ']',
    modifiers: ['ctrlKey'],
  },
  CMD_RIGHT_BRACKET: {
    key: ']',
    modifiers: ['metaKey'],
  },
  CMD_C: {
    key: 'c',
    modifiers: ['metaKey'],
  },
  CTRL_SHIFT_C: {
    key: 'c',
    modifiers: ['shiftKey', 'ctrlKey'],
  },
  CMD_D: {
    key: 'd',
    modifiers: ['metaKey'],
  },
  ALT_SHIFT_D: {
    key: 'd',
    modifiers: ['shiftKey', 'altKey'],
  },
};

export const isKeyMapping = ({
  event,
  mapping,
}: {
  event: SyntheticKeyboardEvent<>,
  mapping: KeyMapping,
}): boolean => {
  const {key, modifiers = []} = mapping;
  return (
    key === event.key && modifiers.every((modifier) => get(event, modifier))
  );
};

export const keyMappingChecker = (event: SyntheticKeyboardEvent<>) => (
  ...mappings: Array<KeyMapping>
) => mappings.some((m) => isKeyMapping({event, mapping: m}));

export const isCharacterEntry = (event: SyntheticKeyboardEvent<>) =>
  event.key.length === 1 && !event.metaKey && !event.ctrlKey;

export const shouldCapture = ({
  isFocused,
  allowInput,
  event,
}: {
  isFocused: boolean,
  allowInput: boolean,
  event: SyntheticKeyboardEvent<>,
}): boolean => {
  if (!isFocused || !allowInput) {
    return false;
  }

  const mappings: Array<KeyMapping> = Object.keys(KEY_MAPPINGS).map(
    (k) => KEY_MAPPINGS[k],
  );
  const isMapped = mappings.some((mapping) => isKeyMapping({event, mapping}));

  return isMapped || isCharacterEntry(event);
};

export const shouldPreventDefault = ({
  isFocused,
  allowInput,
  event,
}: {
  isFocused: boolean,
  allowInput: boolean,
  event: SyntheticKeyboardEvent<>,
}): boolean => {
  const isMapping = keyMappingChecker(event);

  return (
    !isMapping(KEY_MAPPINGS.CMD_C, KEY_MAPPINGS.CTRL_SHIFT_C) &&
    shouldCapture({isFocused, allowInput, event})
  );
};

export const shouldScrollToInput = ({
  isFocused,
  allowInput,
  event,
}: {
  isFocused: boolean,
  allowInput: boolean,
  event: SyntheticKeyboardEvent<>,
}): boolean => {
  const checkMapping = keyMappingChecker(event);

  const isCopying = checkMapping(KEY_MAPPINGS.CTRL_SHIFT_C, KEY_MAPPINGS.CMD_C);
  const isTogglingSuggestions = checkMapping(
    KEY_MAPPINGS.TAB,
    KEY_MAPPINGS.SHIFT_TAB,
  );
  const isModifierKey = ['Shift', 'Ctrl', 'Meta', 'Alt'].includes(event.key);

  const isVisibleChange = !(
    isCopying ||
    isTogglingSuggestions ||
    isModifierKey
  );

  return isFocused && allowInput && isVisibleChange;
};
